const NavigationFields = {
  logo: {
    type: "text",
    label: "Logo URL",
    metadata: {
      source: "context",
      contextKey: "business.logoUrl",
    },
  },
  logoOnDark: {
    type: "text",
    label: "Logo for Transparent Header",
    metadata: {
      source: "manual",
      description:
        "Optional light version of your logo, shown while a transparent header sits over the hero photo. It crossfades back to the normal logo once the header turns solid on scroll.",
    },
  },
  logoText: {
    type: "text",
    label: "Logo Text (Company Name)",
    metadata: {
      source: "context",
      contextKey: "business.name",
    },
  },
  logoDisplay: {
    type: "radio",
    label: "Logo Display",
    options: [
      { label: "Logo only (hide text when logo exists)", value: "logo" },
      { label: "Text only (hide logo)", value: "text" },
      { label: "Both logo and text", value: "both" },
    ],
    metadata: {
      source: "manual",
      description:
        "Controls whether to show the logo image, company name text, or both. If no logo is uploaded, text will always be shown as fallback.",
    },
  },
  links: {
    type: "array",
    label: "Navigation Links",
    metadata: {
      source: "manual", // Navigation structure is usually manual or template-based, not AI generated per-page usually
    },
    getItemSummary: (item: { label: string }) => item.label || "New Link",
    arrayFields: {
      label: { type: "text", label: "Link Text" },
      linkType: {
        type: "radio",
        label: "Link Type",
        options: [
          { label: "Page", value: "page" },
          { label: "URL", value: "url" },
          { label: "Anchor", value: "anchor" },
          { label: "Dropdown", value: "dropdown" },
        ],
      },
      url: {
        type: "custom", // Render function handles this in config
        label: "Destination",
      },
      // A dropdown falls back to a plain link unless it has children, so the
      // "Dropdown" option above is only usable alongside this field. The
      // Renderer supports one level, so children have no children of their own.
      children: {
        type: "array",
        label: "Dropdown Items",
        getItemSummary: (item: { label: string }) => item.label || "New Item",
        arrayFields: {
          label: { type: "text", label: "Link Text" },
          linkType: {
            type: "radio",
            label: "Link Type",
            options: [
              { label: "Page", value: "page" },
              { label: "URL", value: "url" },
              { label: "Anchor", value: "anchor" },
            ],
          },
          url: {
            type: "custom", // Render function handles this in config
            label: "Destination",
          },
        },
        defaultItemProps: {
          label: "New Item",
          linkType: "url",
          url: "/",
        },
      },
    },
    defaultItemProps: {
      label: "New Link",
      linkType: "url",
      url: "/",
      children: [],
    },
  },
  style: {
    type: "select",
    label: "Navigation Style",
    options: [
      { label: "Solid Background", value: "solid" },
      { label: "Transparent (over hero)", value: "transparent" },
      { label: "Sticky (fixed on scroll)", value: "sticky" },
    ],
    metadata: {
      source: "manual",
    },
  },
  ctaStyle: {
    type: "radio",
    label: "Call Button",
    options: [
      { label: "Adaptive", value: "auto" },
      { label: "Always solid", value: "solid" },
    ],
    metadata: {
      source: "manual",
      description:
        "Transparent style only: Adaptive stays quiet over the hero photo and fills with your brand colour once the header turns solid. Always solid keeps the brand button everywhere — louder, better for phone-first businesses.",
    },
  },
  transparentText: {
    type: "radio",
    label: "Text Over Photo",
    options: [
      { label: "Light", value: "light" },
      { label: "Dark", value: "dark" },
    ],
    metadata: {
      source: "manual",
      description:
        "Transparent style only: link and brand color while the header sits over the hero. Pick Dark for bright photos like sky.",
    },
  },
  variant: {
    type: "select",
    label: "Navigation Variant",
    options: [
      { label: "Balanced", value: "balanced" },
      { label: "Compact", value: "compact" },
      { label: "Accented", value: "accented" },
      { label: "Bold", value: "bold" },
    ],
    metadata: {
      source: "manual",
      description:
        "Controls spacing and emphasis. Balanced is safest for long business names. Bold is a dark bar with the call button in your accent colour, to match a bold page.",
    },
  },
  showPhone: {
    type: "radio",
    label: "Show Phone Number",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
    metadata: {
      source: "manual",
    },
  },
  phone: {
    type: "text",
    label: "Phone Number",
    metadata: {
      source: "context",
      contextKey: "business.phone",
    },
  },
  showTextUs: {
    type: "radio",
    label: "Show Text Us Button",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
    metadata: {
      source: "manual",
    },
  },
  showMonogram: {
    type: "radio",
    label: "Show Monogram (when no logo)",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
    metadata: {
      source: "manual",
      description:
        "When enabled, shows an icon with initials derived from the business name when no logo image is set. Disable to show text only.",
    },
  },
  monogramText: {
    type: "text",
    label: "Monogram Override",
    metadata: {
      source: "manual",
      description:
        "Override the auto-generated initials (1–3 characters). Leave blank to auto-derive from business name.",
    },
  },
  monogramStyle: {
    type: "radio",
    label: "Monogram Colour",
    options: [
      { label: "Brand", value: "brand" },
      { label: "Light", value: "light" },
      { label: "Dark", value: "dark" },
    ],
    metadata: {
      source: "manual",
      description:
        "Brand fills with your primary colour. Light and dark are the neutral pair. A transparent header keeps its own translucent treatment either way.",
    },
  },
} as const;

export { NavigationFields };
